import React, { useState, useEffect } from 'react';
import { XMarkIcon, UserGroupIcon, BuildingOfficeIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import LeadAssignmentService from '../services/leadAssignmentService';
import { employeeService } from '../services/employeeService';
import { companyService } from '../services/companyService';
import { departmentService } from '../services/departmentService';
import { useAuth } from '../context/AuthContext';

const EnhancedLeadAssignmentModal = ({ 
  isOpen, 
  onClose, 
  selectedLeads = [], 
  onAssignmentComplete 
}) => {
  const { user } = useAuth();
  
  // State for company-wise organization
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedSalesPerson, setSelectedSalesPerson] = useState('');
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('company'); // 'company', 'department', or 'all'
  
  // Legacy state for backward compatibility
  const [salesDepartments, setSalesDepartments] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadAllData();
      // Auto-select logged-in user's company
      if (user && user.companyId) {
        setSelectedCompany(user.companyId);
      }
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (selectedCompany) {
      loadDepartmentsByCompany(selectedCompany);
    } else {
      setDepartments([]);
      setSelectedDepartment('');
    }
  }, [selectedCompany]);

  // Employee filtering is now done inline - no separate useEffect needed

  // Load all data (companies, departments, employees)
  const loadAllData = async () => {
    setLoadingData(true);
    setError('');
    
    try {
      // Use same API as Employee Management for consistency
      const employeesRes = await employeeService.getAllEmployees();
      const departmentsRes = await departmentService.getAllDepartments();
      
      console.log('📊 Employee Management API response:', employeesRes);
      console.log('📊 Department API response:', departmentsRes);
      
      // Extract data same way as EmployeeManagementWithTabs
      const employeesData = employeesRes?.success ? employeesRes.data : (employeesRes?.data || employeesRes || []);
      const departmentsData = departmentsRes?.data || departmentsRes || [];
      
      setEmployees(Array.isArray(employeesData) ? employeesData : []);
      setDepartments(Array.isArray(departmentsData) ? departmentsData : []);
      
      console.log('📊 Loaded departments:', Array.isArray(departmentsData) ? departmentsData.length : 0);
      console.log('👥 Loaded employees:', Array.isArray(employeesData) ? employeesData.length : 0);
      
      // Set current user's company info
      if (user && user.companyId) {
        setCompanies([{
          _id: user.companyId,
          businessName: user.businessName || 'Your Company'
        }]);
      }
      
      // Also load legacy sales departments for backward compatibility
      const salesResult = await LeadAssignmentService.getSalesDepartments();
      if (salesResult.success) {
        setSalesDepartments(salesResult.data.departments || []);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load employee data');
    }
    
    setLoadingData(false);
  };
  
  // Load departments by company
  const loadDepartmentsByCompany = async (companyId) => {
    try {
      const result = await departmentService.getDepartmentsByCompany(companyId);
      if (result.success) {
        setDepartments(result.data || []);
      }
    } catch (error) {
      console.error('Error loading company departments:', error);
    }
  };
  
  // Employee filtering is now done inline in the component
  
  // Legacy function for backward compatibility (kept for existing functionality)
  // eslint-disable-next-line no-unused-vars
  const loadSalesDepartments = async () => {
    setLoadingData(true);
    setError('');
    
    try {
      const result = await LeadAssignmentService.getSalesDepartments();
      
      if (result.success) {
        setSalesDepartments(result.data.departments || []);
        
        // If no departments found, try to create sample employees
        if (result.data.departments.length === 0) {
          const createResult = await LeadAssignmentService.createSampleEmployees();
          if (createResult.success) {
            // Reload departments after creating sample data
            const reloadResult = await LeadAssignmentService.getSalesDepartments();
            if (reloadResult.success) {
              setSalesDepartments(reloadResult.data.departments || []);
            }
          }
        }
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Error loading sales departments:', error);
      setError('Failed to load sales team');
    } finally {
      setLoadingData(false);
    }
  };

  // Removed legacy functions - using direct filtering now

  const handleAssignment = async () => {
    if (!selectedSalesPerson) {
      setError('Please select a sales person');
      return;
    }

    if (selectedLeads.length === 0) {
      setError('No leads selected for assignment');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const assignmentData = {
        leadIds: selectedLeads.map(lead => lead._id || lead.id),
        assignedTo: selectedSalesPerson,
        assignmentNotes: notes.trim() || `Bulk assignment of ${selectedLeads.length} leads`,
        priority
      };

      const result = await LeadAssignmentService.bulkAssignLeads(assignmentData);

      if (result.success) {
        onAssignmentComplete?.({
          success: true,
          message: `Successfully assigned ${selectedLeads.length} leads`,
          assignedCount: selectedLeads.length
        });
        handleClose();
      } else {
        setError(result.message || 'Assignment failed');
      }
    } catch (error) {
      console.error('Assignment error:', error);
      setError('Failed to assign leads');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCompany('');
    setSelectedDepartment('');
    setSelectedSalesPerson('');
    setPriority('medium');
    setNotes('');
    setError('');
    setViewMode('company');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <UserGroupIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Assign Leads to Sales Team
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Selected Leads Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">
              Selected Leads ({selectedLeads.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {selectedLeads.slice(0, 5).map((lead, index) => (
                <span
                  key={lead._id || lead.id || index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {lead.name}
                </span>
              ))}
              {selectedLeads.length > 5 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  +{selectedLeads.length - 5} more
                </span>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* View Mode Toggle */}
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('company')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'company'
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BuildingOfficeIcon className="h-4 w-4 inline mr-2" />
              By Company
            </button>
            <button
              onClick={() => setViewMode('department')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'department'
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BriefcaseIcon className="h-4 w-4 inline mr-2" />
              By Department
            </button>
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'all'
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserGroupIcon className="h-4 w-4 inline mr-2" />
              All Sales Team
            </button>
          </div>

          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading employee data...</span>
            </div>
          ) : (
            <>
              {/* Company Selection (only in company mode) - Auto-selected for logged-in company */}
              {viewMode === 'company' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🏢 Your Company
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                    {user?.businessName || 'Your Company'} ({employees.length} employees)
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    You can only assign leads to employees in your company
                  </p>
                </div>
              )}
              
              {/* Department Selection */}
              {(viewMode === 'department' || (viewMode === 'company' && user?.companyId)) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🏢 Select Department ({departments.length} available)
                    {viewMode === 'company' && selectedCompany && (
                      <span className="text-xs text-gray-500 ml-2">
                        (from {companies.find(c => c._id === selectedCompany)?.businessName})
                      </span>
                    )}
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => {
                      setSelectedDepartment(e.target.value);
                      setSelectedSalesPerson('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Choose a department...</option>
                    {departments.map((dept) => {
                      const deptEmployees = employees.filter(emp => {
                        // Check if employee belongs to this department
                        const empDeptId = emp.department?._id || emp.department;
                        const empDeptArray = Array.isArray(emp.department) ? emp.department : [empDeptId];
                        return empDeptArray.includes(dept._id) || empDeptArray.some(d => d === dept._id);
                      });
                      
                      return (
                        <option key={dept._id} value={dept._id}>
                          {dept.name} ({deptEmployees.length} employees)
                          {viewMode === 'department' && dept.company && ` - ${companies.find(c => c._id === dept.company)?.businessName || 'Your Company'}`}
                        </option>
                      );
                    })}
                  </select>
                  
                  {departments.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      No departments found for your company
                    </p>
                  )}
                  
                  {departments.length > 0 && (
                    <p className="text-sm text-blue-600 mt-1">
                      ✅ {departments.length} departments loaded successfully
                    </p>
                  )}
                </div>
              )}

              {/* Employee Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  👤 Select Employee
                  {viewMode === 'company' && user?.companyId && !selectedDepartment && (
                    <span className="text-xs text-gray-500 ml-2">
                      (from {user?.businessName || 'Your Company'})
                    </span>
                  )}
                  {selectedDepartment && (
                    <span className="text-xs text-gray-500 ml-2">
                      (from {departments.find(d => d._id === selectedDepartment)?.name} department)
                    </span>
                  )}
                </label>
                <select
                  value={selectedSalesPerson}
                  onChange={(e) => setSelectedSalesPerson(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={(viewMode === 'department' && !selectedDepartment) || (viewMode === 'company' && !user?.companyId)}
                >
                  <option value="">Choose an employee...</option>
                  {(() => {
                    let filteredEmployees = employees;
                    
                    // Filter by department if selected
                    if (selectedDepartment) {
                      filteredEmployees = filteredEmployees.filter(emp => {
                        const empDeptId = emp.department?._id || emp.department;
                        const empDeptArray = Array.isArray(emp.department) ? emp.department : [empDeptId];
                        return empDeptArray.includes(selectedDepartment) || empDeptArray.some(d => d === selectedDepartment);
                      });
                    }
                    
                    return filteredEmployees.map((employee) => {
                      const companyName = employee.company?.businessName || 
                        companies.find(c => c._id === employee.company)?.businessName || 'Your Company';
                      
                      // Handle department name - employee might have multiple departments
                      let departmentName = 'Unknown Department';
                      if (Array.isArray(employee.department)) {
                        const deptNames = employee.department.map(deptId => {
                          const dept = departments.find(d => d._id === deptId);
                          return dept?.name || deptId;
                        });
                        departmentName = deptNames.join(', ');
                      } else {
                        const dept = departments.find(d => d._id === (employee.department?._id || employee.department));
                        departmentName = dept?.name || 'Unknown Department';
                      }
                      
                      return (
                        <option key={employee._id} value={employee._id}>
                          {employee.teamMemberName || employee.name} - {employee.role || 'Employee'}
                          {viewMode === 'all' && ` (${companyName} - ${departmentName})`}
                          {viewMode === 'company' && !selectedDepartment && ` (${departmentName})`}
                          {selectedDepartment && ` (${employee.mobileNumber || employee.phone || 'No phone'})`}
                        </option>
                      );
                    });
                  })()}
                </select>
                
                {viewMode === 'department' && !selectedDepartment && (
                  <p className="text-sm text-gray-500 mt-1">
                    Please select a department first
                  </p>
                )}
                
                {viewMode === 'company' && !user?.companyId && (
                  <p className="text-sm text-gray-500 mt-1">
                    Company information not available
                  </p>
                )}
              </div>

              {/* Priority Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority Level
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Assignment Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assignment Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any specific instructions or notes for the assignment..."
                />
              </div>

              {/* Department Summary */}
              {salesDepartments.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Sales Team Summary
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {salesDepartments.map((dept) => (
                      <div key={dept.department?._id} className="text-sm">
                        <span className="font-medium text-gray-700">
                          {dept.department?.name}:
                        </span>
                        <span className="text-gray-600 ml-2">
                          {dept.employees.length} members
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleAssignment}
            disabled={loading || !selectedSalesPerson || loadingData}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            )}
            {loading ? 'Assigning...' : `Assign ${selectedLeads.length} Lead${selectedLeads.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedLeadAssignmentModal;
